import React, { useState, useEffect } from 'react';
import { X, Image as ImageIcon, Edit2, Trash2 } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { createDateInTimezone } from '../../utils/dateUtils';
import EventFormSkeleton from './views/FormView/EventFormSkeleton';

const MAX_WIDTH = 800;
const QUALITY = 0.7;

const EventForm = ({ mode = 'new', event = null, programs = [], userRole = 'admin', onClose, onSubmit }) => {
  const [order, setOrder] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [type, setType] = useState('event');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [time, setTime] = useState('09:00');
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [program, setProgram] = useState('');
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState('');
  const [errors, setErrors] = useState({});
  const [isPastDate, setIsPastDate] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Utility functions
  const isDateInPast = (dateStr) => {
    if (!dateStr) return false;
    const selectedDate = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return selectedDate < today;
  };

  // Order management state
  const [orderInfo, setOrderInfo] = useState({
    takenOrders: [],
    nextAvailableOrder: 1,
    totalEvents: 0
  });
  const [loadingOrderInfo, setLoadingOrderInfo] = useState(false);
  const [showOrderSuggestion, setShowOrderSuggestion] = useState(false);

  // Fetch order information for the selected program
  const fetchOrderInfo = async (programId) => {
    if (!programId) return;

    setLoadingOrderInfo(true);
    try {
      const response = await fetch(`/api/events/order-info/${programId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setOrderInfo(data);

        // Auto-assign order for new events (always update when program changes)
        if (mode === 'new' && data.nextAvailableOrder) {
          setOrder(data.nextAvailableOrder.toString());
        }
      }
    } catch (error) {
      console.error('Failed to fetch order info:', error);
    } finally {
      setLoadingOrderInfo(false);
    }
  };

  // Check if the current order is taken
  const isOrderTaken = (orderValue) => {
    if (!orderValue || !orderInfo.takenOrders.length) return false;
    const orderNum = Number(orderValue);
    return orderInfo.takenOrders.some(item =>
      item.order === orderNum && item.id !== event?._id
    );
  };

  // Get order conflict information
  const getOrderConflictInfo = (orderValue) => {
    if (!isOrderTaken(orderValue)) return null;
    const orderNum = Number(orderValue);
    return orderInfo.takenOrders.find(item => item.order === orderNum);
  };

  useEffect(() => {
    setIsLoading(true);
    try {
      if (mode === 'edit' || mode === 'view') {
        setOrder(event?.order || '');
        setTitle(event?.title || '');
        setDescription(event?.description || '');
        setIsActive(event?.isActive !== undefined ? event.isActive : true);
        setType(event?.type || 'event');
        
        // Handle date and time extraction
        if (event?.date) {
          const eventDate = new Date(event.date);
          const dateStr = eventDate.toISOString().split('T')[0];
          setDate(dateStr);
          setTime(eventDate.toTimeString().slice(0, 5)); // HH:MM format
          setIsPastDate(isDateInPast(dateStr));
        } else {
          const todayStr = new Date().toISOString().split('T')[0];
          setDate(todayStr);
          setTime('09:00');
          setIsPastDate(false);
        }
        
        setTimezone(event?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
        setProgram(event?.program?._id || '');
        
        if (event?.thumbnail?.url) {
          setThumbnail(event.thumbnail);
          setThumbnailPreview(event.thumbnail.url);
        } else if (event?.thumbnail?.data && event?.thumbnail?.contentType) {
          setThumbnail(event.thumbnail);
          setThumbnailPreview(`data:${event.thumbnail.contentType};base64,${event.thumbnail.data}`);
        }
      }
      setErrors({});
      setShowSuccess(false);
    } finally {
      setIsLoading(false);
    }
  }, [mode, event]);

  // Fetch order info when program changes
  useEffect(() => {
    let programToUse = program;

    // For instructors, we need to get their program
    if (userRole === 'instructor' && !program) {
      // We'll fetch order info after form loads when instructor program is determined
      return;
    }

    if (programToUse) {
      fetchOrderInfo(programToUse);
    }
  }, [program, userRole]);

  // Handle order input changes with real-time validation
  const handleOrderChange = (e) => {
    const value = e.target.value;
    setOrder(value);
    setShowOrderSuggestion(false);

    // Clear order-specific errors when user starts typing
    if (errors.order) {
      setErrors(prev => ({ ...prev, order: null }));
    }

    // Show conflict warning in real-time
    if (value && isOrderTaken(value)) {
      const conflictInfo = getOrderConflictInfo(value);
      setErrors(prev => ({
        ...prev,
        order: `Order ${value} is already used by "${conflictInfo.title}" (${conflictInfo.type})`
      }));
    }
  };

  const isView = mode === 'view';
  const isEdit = mode === 'edit';
  const isNew = mode === 'new';

  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          if (width > MAX_WIDTH) {
            height = Math.round((height * MAX_WIDTH) / width);
            width = MAX_WIDTH;
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob((blob) => {
            resolve(blob);
          }, file.type, QUALITY);
        };
        img.onerror = (error) => reject(error);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleThumbnailChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setErrors({ ...errors, thumbnail: 'Image size must be less than 5MB' });
        return;
      }

      try {
        const compressedBlob = await compressImage(file);
        const reader = new FileReader();
        reader.onload = () => {
          setThumbnail({
            data: reader.result.split(',')[1],
            contentType: file.type
          });
          setThumbnailPreview(reader.result);
        };
        reader.readAsDataURL(compressedBlob);
        setErrors({ ...errors, thumbnail: null });
      } catch (error) {
        console.error('Error processing image:', error);
        setErrors({ ...errors, thumbnail: 'Error processing image. Please try again.' });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = {};
    if (!title.trim()) newErrors.title = 'Title is required';
    if (userRole === 'admin' && !program) newErrors.program = 'Program is required';

    // Validate order field if provided
    if (order && String(order).trim() !== '') {
      const orderNum = Number(order);
      if (!Number.isInteger(orderNum) || orderNum <= 0) {
        newErrors.order = 'Order must be a positive integer';
      } else if (isOrderTaken(order)) {
        const conflictInfo = getOrderConflictInfo(order);
        newErrors.order = `Order ${order} is already used by "${conflictInfo.title}" (${conflictInfo.type})`;
      }
    }
    
    if (type === 'event') {
      if (!date) newErrors.date = 'Date is required for events';
      if (!time) newErrors.time = 'Time is required for events';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setErrors({});
    setIsSubmitting(true);
    
    try {
      const eventData = {
        title,
        description,
        isActive,
        type
      };

      // Only include program for admins (instructors get auto-assigned)
      if (userRole === 'admin' && program) {
        eventData.program = program;
      }

      // Add order if provided and valid
      if (order && String(order).trim() !== '') {
        eventData.order = Number(order);
      }

      if (type === 'event' && date && time) {
        // Create date in the specified timezone and convert to UTC for storage
        const eventDateTime = createDateInTimezone(date, time, timezone);
        eventData.date = eventDateTime.toISOString();
        eventData.timezone = timezone;
      }

      if (thumbnail) {
        eventData.thumbnail = thumbnail;
      }

      try {
        // Call onSubmit (Redux action) and handle any order conflicts
        await onSubmit(eventData);

        setShowSuccess(true);
        setTimeout(() => {
          if (isNew) {
            setOrder('');
            setTitle('');
            setDescription('');
            setIsActive(true);
            setType('event');
            setDate(new Date().toISOString().split('T')[0]);
            setTime('09:00');
            setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
            if (userRole === 'admin') {
              setProgram('');
            }
            setThumbnail(null);
            setThumbnailPreview('');
            setShowSuccess(false);
          }
          onClose();
        }, 1500);
      } catch (error) {
        console.error('Event submission error:', error);

        // Handle specific server errors, particularly order conflicts
        if (error.response) {
          const { status, data } = error.response;

          if (status === 409 && data.type === 'ORDER_CONFLICT') {
            // Handle order conflict from server
            setErrors({ order: data.error });
            // Refresh order info to get latest data
            const currentProgram = userRole === 'admin' ? program :
                                 userRole === 'instructor' ? 'instructor-program' : null;
            if (currentProgram && currentProgram !== 'instructor-program') {
              fetchOrderInfo(currentProgram);
            }
          } else if (data.error) {
            setErrors({ submit: data.error });
          } else {
            setErrors({ submit: 'Failed to save event. Please try again.' });
          }
        } else {
          setErrors({ submit: 'Network error. Please check your connection and try again.' });
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-4xl mx-auto"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-2xl font-bold text-[#054581]">
          {isNew && 'Add New Event/Information'}
          {isEdit && `Edit ${event?.type === 'information' ? 'Information' : 'Event'}`}
          {isView && `View ${event?.type === 'information' ? 'Information' : 'Event'}`}
        </h3>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-[#1086a4] focus:outline-none transition-colors"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      {/* Success Message */}
      {showSuccess && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
          <p className="text-green-700 text-sm font-medium">
            {type === 'information' ? 'Information' : 'Event'} {isNew ? 'created' : 'updated'} successfully!
          </p>
        </div>
      )}

      {/* Error Message */}
      {errors.submit && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-700 text-sm font-medium">{errors.submit}</p>
        </div>
      )}

      {/* Loading Skeleton or Form Content */}
      {isLoading ? (
        <EventFormSkeleton />
      ) : (
        <div className="space-y-6">
              {/* Program Field - Only show for admins */}
              {userRole === 'admin' && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-[#054581] mb-2">Program</label>
                  <select
                    className={`w-full px-3 py-2 bg-[#f9fafb] border rounded-md text-gray-900 ${
                      errors.program
                        ? 'border-red-500 focus:border-red-400'
                        : 'border-[#1086a4] focus:border-[#1086a4]'
                    } focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors`}
                    value={program}
                    onChange={e => setProgram(e.target.value)}
                    disabled={isView || isSubmitting}
                    required
                  >
                    <option value="">Select a program</option>
                    {programs.map(prog => (
                      <option key={prog._id} value={prog._id}>
                        {prog.title}
                      </option>
                    ))}
                  </select>
                  {errors.program && (
                    <p className="text-red-500 text-sm mt-1">{errors.program}</p>
                  )}
                </div>
              )}

              {/* Info message for instructors */}
              {userRole === 'instructor' && (
                <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-sm text-blue-700">
                    Program assignment is automatically handled based on your instructor role.
                  </p>
                </div>
              )}

              {/* Order Field - Enhanced with suggestions */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#054581] mb-2">
                  Order <span className="text-gray-500 text-xs">(Optional)</span>
                  {loadingOrderInfo && (
                    <span className="ml-2 text-xs text-gray-400">Loading order info...</span>
                  )}
                </label>

                <div className="relative">
                  <input
                    type="number"
                    min="1"
                    step="1"
                    className={`w-full px-3 py-2 bg-[#f9fafb] border rounded-md text-gray-900 placeholder-gray-500 ${
                      errors.order
                        ? 'border-red-500 focus:border-red-400'
                        : isOrderTaken(order)
                        ? 'border-yellow-500 focus:border-yellow-400'
                        : 'border-[#1086a4] focus:border-[#1086a4]'
                    } focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors`}
                    value={order}
                    onChange={handleOrderChange}
                    disabled={isView || isSubmitting}
                    placeholder="Enter display order (e.g., 1, 2, 3...)"
                  />

                </div>

                {/* Error messages */}
                {errors.order && (
                  <p className="text-red-500 text-sm mt-1">{errors.order}</p>
                )}

                {/* Order status and suggestions */}
                {!errors.order && orderInfo.totalEvents > 0 && (
                  <div className="mt-2 space-y-1">
                    <p className="text-gray-500 text-xs">
                      Total events in program: {orderInfo.totalEvents}
                      {orderInfo.nextAvailableOrder && (
                        <span className="ml-2 text-[#1086a4]">
                          • Next available: {orderInfo.nextAvailableOrder}
                        </span>
                      )}
                    </p>

                    {/* Show taken orders if there are conflicts or user wants to see them */}
                    {orderInfo.takenOrders.length > 0 && (
                      <details className="text-xs">
                        <summary className="text-gray-500 cursor-pointer hover:text-[#1086a4]">
                          View existing orders ({orderInfo.takenOrders.length})
                        </summary>
                        <div className="mt-1 max-h-20 overflow-y-auto bg-gray-50 rounded px-2 py-1">
                          {orderInfo.takenOrders.map((item, index) => (
                            <div key={index} className="text-gray-600 truncate">
                              {item.order}: {item.title} ({item.type})
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                )}

                <p className="text-gray-500 text-xs mt-1">
                  {mode === 'new'
                    ? 'Auto-assigned based on existing events. You can change it if needed.'
                    : 'Optional number to control the display order of this item'
                  }
                </p>
              </div>

              {/* Type Field */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#054581] mb-2">Type</label>
                <select
                  className={`w-full px-3 py-2 bg-[#f9fafb] border rounded-md text-gray-900 ${
                    errors.type 
                      ? 'border-red-500 focus:border-red-400' 
                      : 'border-[#1086a4] focus:border-[#1086a4]'
                  } focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors`}
                  value={type}
                  onChange={e => setType(e.target.value)}
                  disabled={isView || isSubmitting}
                  required
                >
                  <option value="event">Event</option>
                  <option value="information">Information</option>
                </select>
                {errors.type && (
                  <p className="text-red-500 text-sm mt-1">{errors.type}</p>
                )}
              </div>

              {/* Title Field */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#054581] mb-2">
                  {type === 'information' ? 'Information Title' : 'Event Title'}
                </label>
                <input
                  type="text"
                  className={`w-full px-3 py-2 bg-[#f9fafb] border rounded-md text-gray-900 placeholder-gray-500 ${
                    errors.title 
                      ? 'border-red-500 focus:border-red-400' 
                      : 'border-[#1086a4] focus:border-[#1086a4]'
                  } focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors`}
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  required
                  disabled={isView || isSubmitting}
                  placeholder={`Enter ${type === 'information' ? 'information' : 'event'} title`}
                />
                {errors.title && (
                  <p className="text-red-500 text-sm mt-1">{errors.title}</p>
                )}
              </div>

              {/* Description Field */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#054581] mb-2">
                  {type === 'information' ? 'Information Description' : 'Event Description'}
                </label>
                <div className={`border rounded-md ${
                  errors.description 
                    ? 'border-red-500' 
                    : 'border-[#1086a4]'
                }`}>
                  <ReactQuill
                    value={description}
                    onChange={setDescription}
                    readOnly={isView || isSubmitting}
                    placeholder={`Enter ${type === 'information' ? 'information' : 'event'} description (optional)`}
                    style={{ height: '200px', marginBottom: '42px' }}
                    modules={{
                      toolbar: [
                        [{ 'header': [1, 2, false] }],
                        ['bold', 'italic', 'underline'],
                        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                        ['link', 'image'],
                        ['clean']
                      ]
                    }}
                    formats={[
                      'header', 'bold', 'italic', 'underline',
                      'list', 'bullet', 'link', 'image'
                    ]}
                  />
                </div>
              </div>

              {/* Status Toggle Field */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#054581] mb-2">Status</label>
                <div className="flex items-center space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      const newStatus = !isActive;
                      setIsActive(newStatus);
                      // Clear order field when deactivating
                      if (newStatus === false) {
                        setOrder('');
                      }
                    }}
                    disabled={isView || isSubmitting}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:ring-offset-2 ${
                      isActive ? 'bg-[#1086a4]' : 'bg-gray-200'
                    } ${isView || isSubmitting ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        isActive ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className={`text-sm font-medium ${isActive ? 'text-green-600' : 'text-gray-500'}`}>
                    {isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p className="text-gray-500 text-xs mt-1">
                  {type === 'information' ? 'Inactive information will not be displayed to users' : 'Inactive events will not be displayed to users'}
                  {!isActive && <span className="block text-orange-600 font-medium">Note: Order field will be cleared when deactivated</span>}
                </p>
              </div>

              {/* Date and Time Fields - Only show for events */}
              {type === 'event' && (
                <div className="mb-6 space-y-4">
                  {/* Date and Time Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Date Field */}
                    <div>
                      <label className="block text-sm font-medium text-[#054581] mb-2">Event Date</label>
                      <input
                        type="date"
                        value={date}
                        onChange={e => {
                          const newDate = e.target.value;
                          setDate(newDate);
                          setIsPastDate(isDateInPast(newDate));
                        }}
                        className={`w-full px-3 py-2 bg-[#f9fafb] border rounded-md text-gray-900 placeholder-gray-500 ${
                          errors.date 
                            ? 'border-red-500 focus:border-red-400' 
                            : 'border-[#1086a4] focus:border-[#1086a4]'
                        } focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors`}
                        disabled={isView || isSubmitting}
                      />
                      {errors.date && (
                        <p className="text-red-500 text-sm mt-1">{errors.date}</p>
                      )}
                      {!errors.date && isPastDate && type === 'event' && (
                        <div className="flex items-start mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
                          <div className="flex-shrink-0">
                            <svg className="h-4 w-4 text-amber-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="ml-2">
                            <p className="text-sm text-amber-700">
                              <strong>Past date selected:</strong> This event occurred in the past. It will still be created but may have limited visibility to users.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Time Field */}
                    <div>
                      <label className="block text-sm font-medium text-[#054581] mb-2">Event Time</label>
                      <input
                        type="time"
                        value={time}
                        onChange={e => setTime(e.target.value)}
                        className={`w-full px-3 py-2 bg-[#f9fafb] border rounded-md text-gray-900 placeholder-gray-500 ${
                          errors.time 
                            ? 'border-red-500 focus:border-red-400' 
                            : 'border-[#1086a4] focus:border-[#1086a4]'
                        } focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors`}
                        disabled={isView || isSubmitting}
                        step="900"
                      />
                      {errors.time && (
                        <p className="text-red-500 text-sm mt-1">{errors.time}</p>
                      )}
                    </div>
                  </div>

                  {/* Timezone Field */}
                  <div>
                    <label className="block text-sm font-medium text-[#054581] mb-2">Timezone</label>
                    <select
                      value={timezone}
                      onChange={e => setTimezone(e.target.value)}
                      className={`w-full px-3 py-2 bg-[#f9fafb] border rounded-md text-gray-900 ${
                        errors.timezone 
                          ? 'border-red-500 focus:border-red-400' 
                          : 'border-[#1086a4] focus:border-[#1086a4]'
                      } focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors`}
                      disabled={isView || isSubmitting}
                    >
                      {Intl.supportedValuesOf('timeZone').map(tz => (
                        <option key={tz} value={tz}>
                          {tz.replace('_', ' ')} ({new Date().toLocaleTimeString('en-US', { 
                            timeZone: tz, 
                            timeZoneName: 'short' 
                          }).split(' ').pop()})
                        </option>
                      ))}
                    </select>
                    {errors.timezone && (
                      <p className="text-red-500 text-sm mt-1">{errors.timezone}</p>
                    )}
                  </div>
                </div>
              )}


              {/* Thumbnail Field */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#054581] mb-2">
                  {type === 'information' ? 'Information Thumbnail' : 'Event Thumbnail'}
                </label>
                <div className="mt-2 rounded-lg border border-dashed border-[#1086a4] bg-white">
                  <div className="p-4 sm:p-6 flex flex-col items-center">
                    <div className="w-full max-w-sm mx-auto">
                      {thumbnailPreview ? (
                        <div className="relative aspect-video w-full mb-4 bg-gray-100 rounded-lg overflow-hidden group">
                          <img
                            src={thumbnailPreview}
                            alt="Event thumbnail"
                            className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = `
                                <div class="absolute inset-0 flex items-center justify-center bg-gray-100">
                                  <div class="text-center">
                                    <ImageIcon class="mx-auto h-12 w-12 text-gray-400" />
                                    <p class="mt-2 text-sm text-gray-500">Error loading image</p>
                                  </div>
                                </div>
                              `;
                            }}
                          />
                          {!isView && (
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                              <div className="flex gap-2">
                                <label
                                  htmlFor="thumbnail-upload"
                                  className="cursor-pointer rounded-full bg-white p-2 text-[#1086a4] hover:text-[#054581] transition-colors"
                                  title="Change image"
                                >
                                  <input
                                    id="thumbnail-upload"
                                    name="thumbnail"
                                    type="file"
                                    accept="image/*"
                                    className="sr-only"
                                    onChange={handleThumbnailChange}
                                    disabled={isSubmitting}
                                  />
                                  <Edit2 className="h-5 w-5" />
                                </label>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setThumbnail(null);
                                    setThumbnailPreview('');
                                  }}
                                  className="rounded-full bg-white p-2 text-red-500 hover:text-red-700 transition-colors"
                                  title="Remove image"
                                >
                                  <Trash2 className="h-5 w-5" />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="aspect-video w-full mb-4 bg-gray-50 rounded-lg flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-200">
                          <ImageIcon className="mx-auto h-12 w-12 text-gray-300" />
                          {!isView && (
                            <>
                              <div className="mt-4 flex text-sm leading-6 text-gray-600">
                                <label
                                  htmlFor="thumbnail-upload"
                                  className="relative cursor-pointer rounded-md bg-white font-semibold text-[#1086a4] focus-within:outline-none focus-within:ring-2 focus-within:ring-[#1086a4] focus-within:ring-offset-2 hover:text-[#054581]"
                                >
                                  <span>Upload an image</span>
                                  <input
                                    id="thumbnail-upload"
                                    name="thumbnail"
                                    type="file"
                                    accept="image/*"
                                    className="sr-only"
                                    onChange={handleThumbnailChange}
                                    disabled={isSubmitting}
                                  />
                                </label>
                              </div>
                              <p className="text-xs text-gray-500 mt-2">
                              Recommended: 1:1 ratio rectangle for best display
                              </p>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="text-center mt-2">
                      <p className="text-xs text-gray-500">
                        Supported formats: PNG, JPG, GIF (max. 5MB)
                      </p>
                    </div>
                  </div>
                </div>
                {errors.thumbnail && (
                  <p className="text-red-500 text-sm mt-1">{errors.thumbnail}</p>
                )}
              </div>

          {/* Buttons */}
          {!isView && (
            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2 px-4 border border-[#1086a4] rounded-md text-[#1086a4] font-medium hover:bg-[#f9fafb] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 py-2 px-4 bg-[#1086a4] text-white rounded-md font-medium hover:bg-[#054581] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {isNew ? `Adding ${type === 'information' ? 'Information' : 'Event'}...` : 'Saving Changes...'}
                  </>
                ) : (
                  isNew ? `Add ${type === 'information' ? 'Information' : 'Event'}` : 'Save Changes'
                )}
              </button>
            </div>
          )}
          {isView && (
            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2 px-4 border border-[#1086a4] rounded-md text-[#1086a4] font-medium hover:bg-[#f9fafb] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </div>
      )}
    </form>
  );
};

export default EventForm; 